import { SequenceChangeType } from 'src/types/ViewModel/Stowage/Rotation/SequenceChangeType';

interface HasSequenceNumber {
    [key: string]: any;
    hasChangedSequence?: boolean | SequenceChangeType | null;
}

/**
 * Creates the appropriate hasChangedSequence object for sequence tracking.
 *
 * @param newSequenceNumber The new sequence number being set
 * @param currentItem The current item with potential existing sequence change tracking
 * @param sequenceNumberKey The property name of the sequence number on the current item (e.g., 'berthSequenceNumber')
 * @returns The appropriate hasChangedSequence value: null if back to original, a SequenceChangeType if changed, or true if first change
 */
export const createSequenceChangeValue = <T extends HasSequenceNumber>(
    newSequenceNumber: number,
    currentItem: T | undefined,
    sequenceNumberKey: keyof T
): boolean | SequenceChangeType | null => {
    // Default to true for first changes with no context
    if (!currentItem) {
        return true;
    }

    // Check if we already have tracking in place with the proper object structure
    if (
        currentItem.hasChangedSequence && 
        typeof currentItem.hasChangedSequence === 'object' &&
        currentItem.hasChangedSequence !== null &&
        'originalSequenceNumber' in currentItem.hasChangedSequence
    ) {
        // We have the original sequence number stored
        const originalSequenceNumber = currentItem.hasChangedSequence.originalSequenceNumber;

        // If we've returned to the original sequence, set hasChangedSequence to null
        if (newSequenceNumber === originalSequenceNumber) {
            return null;
        } else {
            // We're still in a changed state, preserve the original
            return {
                originalSequenceNumber,
            };
        }
    } else {
        // First time a sequence change occurs, store the original
        // We need to check if the currentItem has the sequence number property
        const originalSequenceNumber = currentItem[sequenceNumberKey];

        if (typeof originalSequenceNumber === 'number') {
            return {
                originalSequenceNumber: originalSequenceNumber as number,
            };
        }

        // Fallback to true if we couldn't determine the original sequence
        return true;
    }
};
